import { useEffect, useMemo, useState } from "react";

import useLanguage from "../../hooks/useLanguage";
import useDrawer from "../../hooks/useDrawer";
import useContent from "../../hooks/useContent";
import useToaster from "../../hooks/useToaster";
import { tableLayout } from "./tableLayout";

import CommonPage from "../../components/common-page";
import VirtualizedTable from "../../components/virtualTable";
import IButton from "../../components/iconButton";
import ConfDrawer from "./drawers/Drawer";
import MetadataDrawer from "./drawers/MetadataDrawer";
import RowChop from "./components/RowChop";
import ConfigurationsSkeleton from "./Skeleton";
import FileEditDrawer from "./drawers/FileEditDrawer";
import {
  Divider,
  Grid2,
  TextField,
  Typography,
  Tooltip,
  IconButton,
  InputAdornment,
} from "@mui/material";

import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import FlightTakeoffRoundedIcon from "@mui/icons-material/FlightTakeoffRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditAttributesRoundedIcon from "@mui/icons-material/EditAttributesRounded";
import AddIcon from "@mui/icons-material/Add";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import SearchIcon from "@mui/icons-material/Search";
import {
  ChipWrapper,
  ContentGrid,
  MetadataGrid,
  Row,
  ScreenShot,
  ScreenshotContainer,
  ScrollableSection,
  SectionDivider,
  SpacedChip,
  StyledAutocomplete,
  TableWrapper,
} from "./style";
import useMediaQuery from "../../hooks/useMediaQuery";
import { pickMobileColumns } from "../../components/virtualTable/mobileColumns";

// On a phone, keep just Title (wraps) + the row actions.
const MOBILE_COLUMN_WIDTHS = { title: 150, actions: 150 };

const TEMPLATES = [
  {
    label: "Yda",
    value: 0,
  },
  {
    label: "Gama",
    value: 1,
  },
];

const BaseForm = {
  adstxt: "",
  template: 0,
  categories: [],
  robotstxt: "",
  logo: "",
  url: "",
  name: "",
  favicon: "",
  about: "",
  privacy: "",
  contact: "",
  terms: "",
  sitemap: "",
};

export default function Configurations() {
  const {
    listContent,
    setFeatured,
    getSite,
    bindSite,
    republishSite,
    createPage,
    deletePage,
  } = useContent();
  const { addToaster } = useToaster();
  const isMobile = useMediaQuery();
  const [
    {
      pages: { configurations: text },
    },
  ] = useLanguage();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState([]);
  const [siteArticles, setSiteArticles] = useState([]);
  const [form, setForm] = useState(BaseForm);
  const [initForm, setInitForm] = useState(BaseForm);
  const { open, setElement } = useDrawer();
  const siteId = useMemo(() => window.location.pathname.split("/")[2], []);
  const [searchQuery, setSearchQuery] = useState("");

  const TitleText = useMemo(
    () => (isMobile ? text.title.short : text.title.long),
    []
  );
  const addText = useMemo(
    () => (isMobile ? text.add.short : text.add.long),
    []
  );

  const filteredSiteArticles = useMemo(() => {
    if (!searchQuery.trim()) return siteArticles;

    return siteArticles.filter(
      (item) =>
        item.title &&
        item.title.toLowerCase()?.includes(searchQuery.toLowerCase())
    );
  }, [siteArticles, searchQuery]);

  const handleRepublish = async () => {
    try {
      setLoading(true);
      open(false);
      const republishForm = {
        addText: form.addText,
        categories: form.categories,
        template: form.template,
        robotstxt: form.robotstxt,
        adstxt: form.adstxt,
        sitemap: form.sitemap,
        about: form.about,
        privacy: form.privacy,
        contact: form.contact,
        terms: form.terms,
        logo: form.logo,
        favicon: form.favicon,
        url: form.url,
        name: form.name,
      };
      await republishSite(siteId, republishForm);
      addToaster({
        message: text.addQueue,
        severity: "success",
      });
    } catch (err) {
      console.error(err);
      addToaster({
        message: err || text.error,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e?.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCloseDrawer = () => open(false);

  const handleToggleFileEdit = (name) => {
    setElement(
      <FileEditDrawer
        name={name}
        onChange={handleFormChange}
        onClose={handleCloseDrawer}
        value={form[name]}
      />
    );
    open(true);
  };

  const handleMetafilesTrigger = () => {
    setElement(
      <MetadataDrawer
        siteId={siteId}
        onChange={handleFormChange}
        onClose={handleCloseDrawer}
        initValue={{
          logo: form.logo,
          favicon: form.favicon,
          adstxt: form.adstxt,
          robotstxt: form.robotstxt,
        }}
      />
    );
    open(true);
  };

  const handleArticleDrawer = () => {
    setElement(
      <ConfDrawer
        onAdd={handleAddArticle}
        onClose={handleCloseDrawer}
        content={content}
      />
    );
    open(true);
  };

  const handleAddArticle = async (id) => {
    try {
      setLoading(true);
      const updatedSite = await bindSite(siteId, id);
      setContent((prev) => prev.filter((item) => item.id !== id));
      setSiteArticles((prev) => [...prev, updatedSite]);
    } catch (err) {
      console.error(err);
      addToaster({
        message: err || text.error,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const parseTableData = (data) => {
    const proccecedData = data.content?.map((item) => ({
      ...item,
      id: item.contentId,
      category: item.tags,
      url: !!item?.deployedAt
        ? `${data.url}/${item.title.replace(/ /g, "-")}`
        : undefined,
    }));
    return proccecedData;
  };

  const handleFeaturedClick = async (id) => {
    try {
      setLoading(true);
      const updatedSite = await setFeatured(siteId, id);
      setForm(updatedSite);
      window.location.reload();
    } catch (err) {
      console.error(err);
      addToaster({
        message: err || text.error,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const modifyTableLayout = (tableLayout) =>
    Object.keys(tableLayout).reduce((acc, langKey) => {
      acc[langKey] = tableLayout[langKey]?.map((column) => {
        if (column.type === "toggle")
          return { ...column, onClick: handleFeaturedClick };
        return column;
      });
      return acc;
    }, {});

  const handleDuplicateArticles = (data, siteData) => {
    const siteContentIds = siteData.content?.map((item) => item.contentId);
    return data.filter((item) => !siteContentIds?.includes(item.id));
  };

  const handleOnLaunch = async (e) => {
    try {
      setLoading(true);
      const id =
        e?.target?.parentElement?.id ||
        e?.target?.parentElement?.parentElement?.id ||
        e?.target?.parentElement?.parentElement?.parentElement?.id;
      await createPage(siteId, id);
      addToaster({
        message: text.addQueue,
        severity: "success",
      });
    } catch (err) {
      console.error(err);
      addToaster({
        message: err || text.error,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteArticle = async (e) => {
    try {
      setLoading(true);
      const id =
        e?.target?.parentElement?.id ||
        e?.target?.parentElement?.parentElement?.id ||
        e?.target?.parentElement?.parentElement?.parentElement?.id;

      await deletePage(id, siteId);
      window.location.reload();
    } catch (err) {
      console.error(err);
      addToaster({
        message: err || text.error,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const onLoad = async () => {
    setLoading(true);
    try {
      const [data, siteData] = await Promise.all([
        listContent({ page: 1, pageSize: 1000 }),
        getSite(siteId),
      ]);
      setForm(siteData);
      setInitForm(siteData);
      setSiteArticles(parseTableData(siteData));
      setContent(handleDuplicateArticles(data?.items || [], siteData));
    } catch (err) {
      console.error(err);
      addToaster({
        message: err || text.error,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    onLoad();
  }, []);

  if (loading) return <ConfigurationsSkeleton />;
  return (
    <CommonPage
      title={TitleText}
      subtitle={text.subtitle}
      actions={[
        <IButton
          key="add"
          variant="outlined"
          color="action"
          size={isMobile ? "small" : "medium"}
          onClick={handleArticleDrawer}
          startIcon={<AddIcon />}
        >
          {addText}
        </IButton>,
        <IButton
          key="republish"
          variant="contained"
          color="action"
          size={isMobile ? "small" : "medium"}
          startIcon={<RocketLaunchRoundedIcon />}
          onClick={handleRepublish}
        >
          {text.republish}
        </IButton>,
      ]}
    >
      <ContentGrid container spacing={4}>
        <Grid2 size={{ md: 12, lg: 6 }}>
          <ScreenshotContainer>
            <ScreenShot
              src={`https://${form?.url}`}
              title="site"
              width="100%"
              height="100%"
            />
          </ScreenshotContainer>
        </Grid2>

        <MetadataGrid size={{ md: 12, lg: 6 }}>
          <Row center="true" spacebetween="true">
            <Typography variant="h4">{form?.name || text.none}</Typography>
            <Tooltip title={text.editMeta}>
              <IconButton onClick={handleMetafilesTrigger}>
                <EditNoteRoundedIcon />
              </IconButton>
            </Tooltip>
          </Row>

          <Row center="true" spacebetween="true">
            <Typography variant="h6">{text.template}:</Typography>
            <StyledAutocomplete
              name="template"
              disablePortal
              onChange={(_, { value }) =>
                handleFormChange({
                  target: { value: value, name: "template" },
                })
              }
              value={TEMPLATES[form.template]}
              options={TEMPLATES}
              renderInput={(params) => <TextField {...params} />}
            />
          </Row>

          <Row center="true" spacebetween="true">
            <Typography variant="h6">{text.categories}:</Typography>
            <ChipWrapper>
              {form?.categories?.map((category) => (
                <SpacedChip
                  key={category}
                  id={category}
                  label={category}
                />
              )) || text.none}
            </ChipWrapper>
          </Row>

          <Divider />

          <ScrollableSection>
            <RowChop
              form={initForm}
              name="robotstxt"
              label={text.txtDrawer.robots}
              handleToggleFileEdit={handleToggleFileEdit}
            />
            <RowChop
              form={initForm}
              name="adstxt"
              label={text.txtDrawer.ads}
              handleToggleFileEdit={handleToggleFileEdit}
            />
            <RowChop
              form={initForm}
              name="sitemap"
              label={text.txtDrawer.sitemap}
              handleToggleFileEdit={handleToggleFileEdit}
            />
            <RowChop
              form={initForm}
              name="about"
              label={text.txtDrawer.about}
              handleToggleFileEdit={handleToggleFileEdit}
            />
            <RowChop
              form={initForm}
              name="privacy"
              label={text.txtDrawer.privacy}
              handleToggleFileEdit={handleToggleFileEdit}
            />
            <RowChop
              form={initForm}
              name="contact"
              label={text.txtDrawer.contact}
              handleToggleFileEdit={handleToggleFileEdit}
            />
            <RowChop
              form={initForm}
              name="terms"
              label={text.txtDrawer.terms}
              handleToggleFileEdit={handleToggleFileEdit}
            />
          </ScrollableSection>
        </MetadataGrid>
      </ContentGrid>
      <SectionDivider />
      <TableWrapper>
        <TextField
          name="search"
          fullWidth
          variant="outlined"
          size="small"
          placeholder={text.search.placeholder}
          value={searchQuery}
          onChange={handleSearchChange}
          style={{ marginBottom: "16px" }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />

        <VirtualizedTable
          columns={
            isMobile
              ? pickMobileColumns(modifyTableLayout(tableLayout), MOBILE_COLUMN_WIDTHS)
              : modifyTableLayout(tableLayout)
          }
          data={filteredSiteArticles}
          height="100%"
          minHeight={"400px"}
          actions={[
            <Tooltip title={text.republish}>
              <IconButton color="action" onClick={handleOnLaunch}>
                <FlightTakeoffRoundedIcon />
              </IconButton>
            </Tooltip>,
            <Tooltip title={text.edit} onClick={() => {}}>
              <IconButton color="info">
                <EditAttributesRoundedIcon />
              </IconButton>
            </Tooltip>,
            <Tooltip title={text.delete} onClick={handleDeleteArticle}>
              <IconButton color="error">
                <DeleteRoundedIcon />
              </IconButton>
            </Tooltip>,
          ]}
        />
      </TableWrapper>
    </CommonPage>
  );
}
